import { Relation, type TableAutomationGroupRuleMatchingItem, type TableAutomationGroupRulePayloadItem } from '@/api'
import { getReferentialColumnDisplayLabel } from './referentialColumnLabel'
import { padRow } from './referentialGrid'

export const REFERENTIAL_ACTION_FILL_IF_EMPTY = '__only_if_empty__'
export const REFERENTIAL_COLUMN_LINK = '__column_link__'

export type ReferentialActionMode = 'replace' | 'fill_if_empty'

export type ReferentialColumnOption = {
  label: string
  value: string
}

export type ReferentialRelationOption = {
  label: string
  value: Relation
}

export function sanitizePayloadRule(
  rule: TableAutomationGroupRulePayloadItem
): TableAutomationGroupRulePayloadItem {
  const { color: _color, ...rest } = rule
  return rest
}

function payloadRuleKey(rule: TableAutomationGroupRulePayloadItem): string {
  return `${rule.columnIndex ?? ''}:${rule.decEntryId ?? ''}`
}

export function isColumnLinkRule(
  rule: TableAutomationGroupRulePayloadItem
): boolean {
  return rule.customValue === REFERENTIAL_COLUMN_LINK
}

export function buildColumnLinkRule(
  columnIndex: number,
  decEntryId: number
): TableAutomationGroupRulePayloadItem {
  return {
    columnIndex,
    decEntryId,
    customValue: REFERENTIAL_COLUMN_LINK,
  }
}

export function getAutomationActionsFromPayload(
  payload: TableAutomationGroupRulePayloadItem[] = [],
  matching: TableAutomationGroupRuleMatchingItem[] = []
): TableAutomationGroupRulePayloadItem[] {
  if (matching.length === 0) {
    return []
  }

  return payload
    .filter((rule) => !isColumnLinkRule(rule))
    .map(sanitizePayloadRule)
}

export function splitPayloadRules(
  payload: TableAutomationGroupRulePayloadItem[] = [],
  matching: TableAutomationGroupRuleMatchingItem[] = []
): {
  columnLinks: TableAutomationGroupRulePayloadItem[]
  actions: TableAutomationGroupRulePayloadItem[]
} {
  const sanitized = payload.map(sanitizePayloadRule)

  if (matching.length === 0) {
    return { columnLinks: sanitized, actions: [] }
  }

  return {
    columnLinks: sanitized.filter(isColumnLinkRule),
    actions: sanitized.filter((rule) => !isColumnLinkRule(rule)),
  }
}

export function mergePayloadRules(
  columnLinks: TableAutomationGroupRulePayloadItem[],
  actions: TableAutomationGroupRulePayloadItem[]
): TableAutomationGroupRulePayloadItem[] {
  const merged = new Map<string, TableAutomationGroupRulePayloadItem>()

  for (const rule of columnLinks.map(sanitizePayloadRule)) {
    merged.set(payloadRuleKey(rule), rule)
  }

  for (const rule of actions.map(sanitizePayloadRule)) {
    merged.set(payloadRuleKey(rule), rule)
  }

  return Array.from(merged.values()).sort(
    (left, right) => (left.columnIndex ?? 0) - (right.columnIndex ?? 0)
  )
}

export function getActionMode(
  rule: TableAutomationGroupRulePayloadItem
): ReferentialActionMode {
  return rule.customValue === REFERENTIAL_ACTION_FILL_IF_EMPTY
    ? 'fill_if_empty'
    : 'replace'
}

export function withActionMode(
  rule: TableAutomationGroupRulePayloadItem,
  mode: ReferentialActionMode
): TableAutomationGroupRulePayloadItem {
  if (mode === 'fill_if_empty') {
    return sanitizePayloadRule({
      ...rule,
      customValue: REFERENTIAL_ACTION_FILL_IF_EMPTY,
    })
  }

  const { customValue: _customValue, ...rest } = sanitizePayloadRule(rule)
  return rest
}

export function isMatchingRuleComplete(
  rule?: TableAutomationGroupRuleMatchingItem
): boolean {
  return (
    rule?.decEntryId != null &&
    !!rule.relation &&
    rule.columnIndex != null
  )
}

export function isActionRuleComplete(
  rule?: TableAutomationGroupRulePayloadItem
): boolean {
  return rule?.decEntryId != null && rule.columnIndex != null
}

export function canSaveAutomationRules(
  matching: TableAutomationGroupRuleMatchingItem[],
  actions: TableAutomationGroupRulePayloadItem[]
): boolean {
  if (!matching.length) {
    return false
  }

  return (
    matching.every(isMatchingRuleComplete) &&
    actions.every(isActionRuleComplete)
  )
}

export function buildReferentialColumnOptions(
  headers: string[],
  columnCount: number
): ReferentialColumnOption[] {
  return padRow(headers, columnCount).map((header, index) => ({
    label: getReferentialColumnDisplayLabel(header, index),
    value: String(index),
  }))
}

export function buildSimplifiedAutomationRules(
  columnLinks: TableAutomationGroupRulePayloadItem[]
): {
  matching: TableAutomationGroupRuleMatchingItem[]
  actions: TableAutomationGroupRulePayloadItem[]
} {
  const matching: TableAutomationGroupRuleMatchingItem[] = []
  const actions: TableAutomationGroupRulePayloadItem[] = []

  columnLinks.forEach((link) => {
    if (link.decEntryId == null || link.columnIndex == null) {
      return
    }

    matching.push({
      decEntryId: link.decEntryId,
      columnIndex: link.columnIndex,
      relation: Relation.Eq,
    })

    actions.push({
      decEntryId: link.decEntryId,
      columnIndex: link.columnIndex,
    })
  })

  return { matching, actions }
}

export function areMatchingRulesEqual(
  left: TableAutomationGroupRuleMatchingItem[],
  right: TableAutomationGroupRuleMatchingItem[]
): boolean {
  if (left.length !== right.length) {
    return false
  }

  return left.every((rule, index) => {
    const other = right[index]
    return (
      rule.decEntryId === other.decEntryId &&
      rule.columnIndex === other.columnIndex &&
      rule.relation === other.relation &&
      (rule.customValue ?? null) === (other.customValue ?? null)
    )
  })
}

export function areActionRulesEqual(
  left: TableAutomationGroupRulePayloadItem[],
  right: TableAutomationGroupRulePayloadItem[]
): boolean {
  if (left.length !== right.length) {
    return false
  }

  return left.every((rule, index) => {
    const other = right[index]
    return (
      rule.decEntryId === other.decEntryId &&
      rule.columnIndex === other.columnIndex &&
      getActionMode(rule) === getActionMode(other)
    )
  })
}
